import type {
  ContactAddress,
  ContactAddressKind,
  ContactDetail,
  ContactEmail,
  ContactInput,
  ContactKind,
  ContactPerson,
  ContactPhone,
  ContactType,
  ContactWebsite,
  ContactWebsiteKind,
} from '../api/business/types';

/** A list row as the form edits it: every text '' where there is none. */
export type ContactEmailDraft = { email: string; label: string; note: string; isPrimary: boolean };

export type ContactPhoneDraft = { phone: string; label: string; note: string; isPrimary: boolean };

export type ContactAddressDraft = {
  kind: ContactAddressKind;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  note: string;
};

export type ContactPersonDraft = { name: string; role: string; email: string; phone: string; note: string };

export type ContactWebsiteDraft = { url: string; kind: ContactWebsiteKind };

/** A contact's card as its form edits it (R0039): texts as typed, every list in its order. */
export type ContactDraft = {
  kind: ContactKind;
  type: ContactType;
  name: string;
  legalName: string;
  ico: string;
  dic: string;
  icDph: string;
  doNotContact: boolean;
  description: string;
  emails: Array<ContactEmailDraft>;
  phones: Array<ContactPhoneDraft>;
  addresses: Array<ContactAddressDraft>;
  persons: Array<ContactPersonDraft>;
  websites: Array<ContactWebsiteDraft>;
  labelIds: Array<string>;
};

const text = (value: string | null): string => value ?? '';

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export const emptyEmail = (isPrimary: boolean): ContactEmailDraft => ({
  email: '',
  label: '',
  note: '',
  isPrimary,
});

export const emptyPhone = (isPrimary: boolean): ContactPhoneDraft => ({
  phone: '',
  label: '',
  note: '',
  isPrimary,
});

export const emptyAddress = (kind: ContactAddressKind): ContactAddressDraft => ({
  kind,
  street: '',
  city: '',
  postalCode: '',
  country: '',
  note: '',
});

export const emptyPerson = (): ContactPersonDraft => ({ name: '', role: '', email: '', phone: '', note: '' });

export const emptyWebsite = (): ContactWebsiteDraft => ({ url: '', kind: 'WEB' });

/** The form of a card — empty for a new contact, a customer organisation. */
export const contactDraftOf = (detail: ContactDetail | null): ContactDraft =>
  detail === null
    ? {
        kind: 'ORGANISATION',
        type: 'CUSTOMER',
        name: '',
        legalName: '',
        ico: '',
        dic: '',
        icDph: '',
        doNotContact: false,
        description: '',
        emails: [],
        phones: [],
        addresses: [],
        persons: [],
        websites: [],
        labelIds: [],
      }
    : {
        kind: detail.kind,
        type: detail.type,
        name: detail.name,
        legalName: text(detail.legalName),
        ico: text(detail.ico),
        dic: text(detail.dic),
        icDph: text(detail.icDph),
        doNotContact: detail.doNotContact,
        description: text(detail.description),
        emails: detail.emails.map((row: ContactEmail) => ({
          ...row,
          label: text(row.label),
          note: text(row.note),
        })),
        phones: detail.phones.map((row: ContactPhone) => ({
          ...row,
          label: text(row.label),
          note: text(row.note),
        })),
        addresses: detail.addresses.map((row: ContactAddress) => ({
          kind: row.kind,
          street: text(row.street),
          city: text(row.city),
          postalCode: text(row.postalCode),
          country: text(row.country),
          note: text(row.note),
        })),
        persons: detail.persons.map((row: ContactPerson) => ({
          name: row.name,
          role: text(row.role),
          email: text(row.email),
          phone: text(row.phone),
          note: text(row.note),
        })),
        websites: detail.websites.map((row: ContactWebsite) => ({ ...row })),
        labelIds: detail.labels.map((label) => label.id),
      };

/**
 * The card to save: texts trimmed, '' as null, a row with nothing in its main field left out. The API makes one
 * e-mail and one phone primary and one address billing; the form keeps them so already, `markPrimary` and
 * `markBilling` saying which.
 */
export const contactInputOf = (draft: ContactDraft): ContactInput => ({
  kind: draft.kind,
  type: draft.type,
  name: draft.name.trim(),
  legalName: orNull(draft.legalName),
  ico: orNull(draft.ico),
  dic: orNull(draft.dic),
  icDph: orNull(draft.icDph),
  doNotContact: draft.doNotContact,
  description: orNull(draft.description),
  emails: draft.emails
    .filter((row) => row.email.trim() !== '')
    .map((row) => ({
      email: row.email.trim(),
      label: orNull(row.label),
      note: orNull(row.note),
      isPrimary: row.isPrimary,
    })),
  phones: draft.phones
    .filter((row) => row.phone.trim() !== '')
    .map((row) => ({
      phone: row.phone.trim(),
      label: orNull(row.label),
      note: orNull(row.note),
      isPrimary: row.isPrimary,
    })),
  addresses: draft.addresses
    .map((row) => ({
      kind: row.kind,
      street: orNull(row.street),
      city: orNull(row.city),
      postalCode: orNull(row.postalCode),
      country: orNull(row.country.toUpperCase()),
      note: orNull(row.note),
    }))
    .filter(
      (row) => row.street !== null || row.city !== null || row.postalCode !== null || row.country !== null,
    ),
  persons: draft.persons
    .filter((row) => row.name.trim() !== '')
    .map((row) => ({
      name: row.name.trim(),
      role: orNull(row.role),
      email: orNull(row.email),
      phone: orNull(row.phone),
      note: orNull(row.note),
    })),
  websites: draft.websites
    .filter((row) => row.url.trim() !== '')
    .map((row) => ({ url: row.url.trim(), kind: row.kind })),
  labelIds: draft.labelIds,
});

/** Row `index` becomes the primary one, and no other is. */
export const markPrimary = <TRow extends { isPrimary: boolean }>(
  rows: Array<TRow>,
  index: number,
): Array<TRow> => rows.map((row, at) => ({ ...row, isPrimary: at === index }));

/** Address `index` becomes the billing one; the one that was billing becomes operating. */
export const markBilling = (rows: Array<ContactAddressDraft>, index: number): Array<ContactAddressDraft> =>
  rows.map((row, at) => ({ ...row, kind: at === index ? 'BILLING' : 'OPERATING' }));

/** A row removed; when it was the primary one, the first left becomes primary. */
export const removePrimaryRow = <TRow extends { isPrimary: boolean }>(
  rows: Array<TRow>,
  index: number,
): Array<TRow> => {
  const left = rows.filter((_row, at) => at !== index);

  return left.some((row) => row.isPrimary) ? left : markPrimary(left, 0);
};

/** What `href` opens: a mail, a call, or the address as written — with a scheme when it has none. */
export const websiteHref = (url: string): string => (/^https?:\/\//i.test(url) ? url : `https://${url}`);
